<template>
  <b-card no-body>
    <b-card-body id="providers-card-body">
      <providers-filter :values="filterValues" :filter-submitting="isLoading" @filter="setFilter" />

      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :rows="rows"
        :columns="getColumns"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <table-no-result-found slot="emptystate" />
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'logo'" class="d-flex justify-content-center">
            <img
              :src="getProviderLogo(props.row)"
              class="provider-logo"
              style="max-width: 50px; max-height: 50px"
              alt="Logo"
            />
          </span>
          <span v-if="props.column.field === 'name'">
            {{ props.row.name || '-' }}
          </span>
          <span v-if="props.column.field === 'accountIdentification'">
            {{ props.row.account_identification || '-' }}
          </span>
          <span v-if="props.column.field === 'regionModelText'">
            {{ getProviderModelTypeText(props.row.region_model_type) || '-' }}
          </span>

          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                :id="`edit-provider-button-${props.row.id}`"
                variant="warning"
                type="button"
                size="sm"
                @click="infoProvider(props.row.id, props.row.name)"
              >
                <feather-icon icon="Edit3Icon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`edit-provider-button-${props.row.id}`"
                  :title="t('Edit')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
            </span>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
  </b-card>
</template>

<script>
import { BButton, BCard, BCardBody, BTooltip } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import ProvidersFilter from '@/views/pages/providers/ProvidersFilters.vue';
import { getProviderLogo } from '@/libs/providerHelper';
import useService from '@/auth/service/useService';
import { mapGetters } from 'vuex';
import TableNoResultFound from '@/components/TableNoResultFound.vue';

export default {
  components: {
    TableNoResultFound,
    BTooltip,
    BButton,
    VueGoodTable,
    BCard,
    BCardBody,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    ProvidersFilter
  },

  mixins: [helper],

  data() {
    return {
      isLoading: false,
      rows: [],
      details: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        name: ''
      },
      defaultFilterValues: {
        name: ''
      }
    };
  },
  computed: {
    ...mapGetters({
      regionModelType: 'app/regionModelType'
    }),
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('Logo'),
          field: 'logo'
        },
        {
          label: this.$t('Company Name'),
          field: 'name'
        },
        {
          label: this.$t('Account Number'),
          field: 'accountIdentification'
        },
        {
          label: this.$t('Regionalization Model Structure'),
          field: 'regionModelText'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },

  async mounted() {
    this.loadItems();

    if (this.regionModelType.length === 0) {
      await this.$store.dispatch('app/getRegionModelType');
    }
  },

  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    // load items is what brings back the rows from server
    loadItems() {
      let requestData = this.filter;

      requestData = {
        ...requestData,
        ...this.getFilteredObject(this.filterValues)
      };

      this.isLoading = true;

      useService
        .getAllProviders(requestData)
        .then(async response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });

      if (
        requestData.dateFrom &&
        requestData.dateTo &&
        new Date(requestData.dateFrom) > new Date(requestData.dateTo)
      ) {
        this.showErrorMessage(this.$t('Big Date Error'), this.$t('Unsuccessful'));
        return false;
      }

      return true;
    },
    setFilter(data) {
      this.filterValues = data
        ? { ...this.defaultFilterValues, ...data }
        : { ...this.defaultFilterValues };
      this.filter.page = 1;
      this.loadItems();
    },

    async providerRegions(id) {
      this.$router.push(`/providers/${id}/regions`);
    },

    async infoProvider(id) {
      this.$router.push(`/providers/${id}/regions`);
    },

    async allFees(id) {
      this.$router.push(`/providers/${id}/additional-fees`);
    },

    getProviderModelTypeText(type) {
      const model = this.regionModelType.find(item => parseInt(item.value) === parseInt(type));
      return model ? model.text : '-';
    },
    getProviderLogo
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
